/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, getter-return, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import Relationship from '@linc-technologies/linced-common/enums/contact-relationship';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { action, computed } from '@ember/object';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import { alias } from '@ember/object/computed';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import config from 'linced-app/config/environment';
import { classNameBindings } from '@ember-decorators/component';

@classNameBindings('contact.currentState.isDirty:md-dirty')
export default class ContactCard extends Component {
  @service
  fetch;

  @service
  session;

  @service
  store;

  @service
  toaster;

  Relationship = Relationship;
  Jurisdiction = Jurisdiction;
  Scopes = Scopes;

  @alias('contact.caregiver')
  caregiverPersonRecord;

  @alias('session.school.jurisdiction')
  jurisdiction;

  editable = true;
  removable = true;

  @computed('caregiverPersonRecord.fields.core:emailAddress')
  get hasUserAccount() {
    if (!isEmpty(this.caregiverPersonRecord)) {
      return !isEmpty(
        this.caregiverPersonRecord.get('fields.core:emailAddress.valueString'),
      );
    }
    return false;
  }

  @computed('caregiverPersonRecord.caregiver.learnerIds')
  get caregiverLearnerIds() {
    return this.caregiverPersonRecord.get('caregiver.learnerIds');
  }

  @computed('caregiverLearnerIds.[]')
  get numberOfLearners() {
    if (!isEmpty(this.caregiverLearnerIds)) {
      return this.caregiverLearnerIds.length;
    }
  }

  @computed('hasUserAccount', 'caregiverLearnerIds.[]')
  get learners() {
    if (this.hasUserAccount) {
      if (!isEmpty(this.caregiverLearnerIds)) {
        let learners = [];
        let thisLearnerId = this.get('learner.id');
        this.caregiverLearnerIds.forEach((learnerId) => {
          // If it's the student whose contact this is, use the record which was
          // passed in - so we don't unexpectedly reload them when adding a new
          // contact if that contact's already connected to them elsewhere.
          if (learnerId === thisLearnerId) {
            learners.push(this.learner);
            return;
          }
          // Otherwise, try fetching the student.
          this.store.findRecord('person', learnerId).then((learner) => {
            learners.push(learner);
          });
        });
        return learners;
      }
    }
    return [];
  }

  @computed('learners.[]')
  get learnerMap() {
    let learnerMapList = [];
    if (!isEmpty(this.learners)) {
      this.learners.forEach((person) => {
        if (
          this.get('learner.id') !== person.id &&
          person.enrolmentStatus !== EnrolmentStatus.ARCHIVED
        ) {
          let learner = person.learner;
          if (!isEmpty(learner) && !isEmpty(learner.contacts)) {
            let contact = findBy(
              learner.contacts,
              'caregiverId',
              this.get('contact.caregiverId'),
            );
            if (!isEmpty(contact)) {
              learnerMapList.push({
                person,
                fullName: person.fullName,
                isAccessEnabled: contact.allowedAccess,
                isBillPayer: contact.billPayer,
                relationshipName: contact.relationshipName,
              });
            }
          }
        }
      });
    }
    return learnerMapList;
  }

  @computed('learners.length', 'numberOfLearners')
  get learnerCountAtAnotherSchool() {
    return this.numberOfLearners - this.learners.length;
  }

  @computed('caregiverPersonRecord', 'jurisdiction')
  get caregiverAddress() {
    if (!isEmpty(this.caregiverPersonRecord)) {
      let fieldSuffix = 'core';
      switch (this.jurisdiction) {
        case Jurisdiction.NZ_MOE:
          fieldSuffix = 'nzMOE';
          break;
      }
      let address1 = this.caregiverPersonRecord.get(
        `fields.${fieldSuffix}:address1.valueString`,
      );
      let address2 = this.caregiverPersonRecord.get(
        `fields.${fieldSuffix}:address2.valueString`,
      );
      let address3 = this.caregiverPersonRecord.get(
        `fields.${fieldSuffix}:address3.valueString`,
      );
      let address4 = this.caregiverPersonRecord.get(
        `fields.${fieldSuffix}:address4.valueString`,
      );
      let postcode = this.caregiverPersonRecord.get(
        `fields.${fieldSuffix}:postcode.valueString`,
      );
      let moreAddress = [address2, address3, address4]
        .filter((addr) => {
          return !isEmpty(addr);
        })
        .join(', ');
      if (isEmpty(address1) && isEmpty(moreAddress) && isEmpty(postcode)) {
        return;
      }
      return {
        address1,
        moreAddress,
        postcode,
      };
    }
  }

  @computed('jurisdiction', 'contact.fields.core:livesWith.value')
  get showLivesWithIcon() {
    if (this.jurisdiction !== Jurisdiction.AU_NSW) {
      // Only show the "Lives With" icon for NSW schools.
      return false;
    }

    return this.contact.get('fields.core:livesWith.value');
  }

  @computed(
    'jurisdiction',
    'contact.fields.{core:custodyRestriction.value,core:nonAVOCustodyRestriction.value,contact.fields.core:custodyAlert.value}',
  )
  get hasCustodyOrder() {
    if (this.jurisdiction !== Jurisdiction.AU_NSW) {
      // This process only applies to NSW schools.
      return false;
    }

    let avoRestriction = this.contact.get(
      'fields.core:custodyRestriction.value',
    );
    let nonAvoRestriction = this.contact.get(
      'fields.core:nonAVOCustodyRestriction.value',
    );
    let custodyAlert = this.contact.get('fields.core:custodyAlert.value');

    return avoRestriction || nonAvoRestriction || !isEmpty(custodyAlert);
  }

  @computed('hasCustodyOrder', 'contact.fields.core:custodyAlert.value')
  get custodyOrderMessage() {
    if (!this.hasCustodyOrder) {
      return '';
    }
    return (
      this.contact.get('fields.core:custodyAlert.value') ??
      'Custody Order Exists'
    );
  }

  @computed(
    'jurisdiction',
    'contact.fields.core:receivesAssessmentReport.value',
  )
  get showReportIcon() {
    if (this.jurisdiction !== Jurisdiction.AU_NSW) {
      // Only show the "Receives Assessment Report" icon for NSW schools.
      return false;
    }

    return this.contact.get('fields.core:receivesAssessmentReport.value');
  }

  @computed('session.user.roles')
  get hasAdminAccess() {
    return this.session.user.roles.includes('adminsite:fullaccess');
  }

  @action
  openMailto(email) {
    window.open(`mailto:${email}`, 'mail');
  }

  @action
  testNotification() {
    let url = `${config.APP.apiHost}/notifications/${config.APP.apiPath}/test`;
    let data = {
      schoolId: this.session.school.id,
      firstName: this.caregiverPersonRecord.get('firstName'),
      lastName: this.caregiverPersonRecord.get('lastName'),
      personId: this.caregiverPersonRecord.get('id'),
    };
    let options = {
      data: JSON.stringify(data),
    };
    this.fetch
      .post(url, options)
      .then(() => {
        this.toaster.success(`Successfully sent test notification`);
      })
      .catch(() => {
        this.toaster.error('Error sending test notification');
      });
  }
}
